import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  adminListLibraries,
  createProduct,
  deleteProduct,
  getProduct,
  updateProduct,
} from '../../services/api';
import type { PrivateLibrary, Product } from '../../types';
import ProductImagesEditor from '../../components/admin/ProductImagesEditor';
import DropdownWithAddNew from '../../components/admin/DropdownWithAddNew';
import MultiSelectWithAddNew from '../../components/admin/MultiSelectWithAddNew';
import { useMasterOptions } from '../../hooks/useMasterOptions';
import { useConfirm } from '../../hooks/useConfirm';
import { useDraftAutosave } from '../../hooks/useDraftAutosave';
import RestoredDraftBanner from '../../components/admin/RestoredDraftBanner';
import MoneyInput from '../../components/admin/MoneyInput';
import ProductListingPreview from '../../components/admin/ProductListingPreview';

type Mode = 'new' | 'edit';

const blankProduct = (): Partial<Product> => ({
  name: '',
  slug: '',
  description: '',
  category: '',
  status: 'draft',
  images: [],
  price_cents: 0,
  weight_oz: 0,
  in_stock: true,
  size_options: [],
  color_options: [],
  thread_color_options: [],
  font_options: [],
  size_price_overrides: {},
  image_color_map: {},
});

const dollarsToCents = (s: string): number => {
  const n = Number(s);
  if (!isFinite(n) || n < 0) return 0;
  return Math.round(n * 100);
};

const centsToDollars = (c: number): string => (c / 100).toFixed(2);

const ProductEditor: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const mode: Mode = productId ? 'edit' : 'new';

  const [product, setProduct] = useState<Partial<Product>>(blankProduct());
  const [libraries, setLibraries] = useState<PrivateLibrary[]>([]);
  const { options: masterOptions, addOption } = useMasterOptions();
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { confirm, confirmDialog } = useConfirm();
  const [savedHint, setSavedHint] = useState<string | undefined>();
  // The last thing the server confirmed, so a restored draft can be
  // thrown away without reloading the page.
  const [serverCopy, setServerCopy] = useState<Partial<Product>>(blankProduct());

  const { restoredAt, commit, discard } = useDraftAutosave<Partial<Product>>({
    key: `product:${productId ?? 'new'}`,
    value: product,
    ready: !loading,
    onRestore: setProduct,
  });

  useEffect(() => {
    adminListLibraries().then(setLibraries).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !productId) return;
    setLoading(true);
    getProduct(productId)
      .then(p => {
        setProduct(p);
        setServerCopy(p);
      })
      .catch(e => setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load'))
      .finally(() => setLoading(false));
  }, [mode, productId]);

  const flashSaved = (msg: string) => {
    setSavedHint(msg);
    window.setTimeout(() => setSavedHint(undefined), 2000);
  };

  const saveProduct = async () => {
    setSaving(true);
    setError(undefined);
    try {
      // Drop stale size_price_overrides entries for sizes that are no
      // longer selected, and stale image_color_map entries for removed
      // images / removed colors.
      const sizeOpts = product.size_options ?? [];
      const trimmedOverrides: Record<string, number> = {};
      for (const [k, v] of Object.entries(product.size_price_overrides ?? {})) {
        if (sizeOpts.includes(k) && typeof v === 'number' && v >= 0) {
          trimmedOverrides[k] = v;
        }
      }

      const imgs = product.images ?? [];
      const colorOpts = product.color_options ?? [];
      const trimmedColorMap: Record<string, string> = {};
      for (const [url, color] of Object.entries(product.image_color_map ?? {})) {
        if (imgs.includes(url) && colorOpts.includes(color)) {
          trimmedColorMap[url] = color;
        }
      }

      const payload = {
        name: product.name,
        slug: product.slug,
        description: product.description,
        category: product.category,
        status: product.status,
        images: imgs,
        price_cents: product.price_cents ?? 0,
        weight_oz: product.weight_oz ?? 0,
        in_stock: product.in_stock ?? true,
        size_options: sizeOpts,
        color_options: colorOpts,
        thread_color_options: product.thread_color_options ?? [],
        font_options: product.font_options ?? [],
        size_price_overrides: trimmedOverrides,
        image_color_map: trimmedColorMap,
        library_slug: product.library_slug ?? '',
      };
      if (mode === 'new') {
        const created = await createProduct(payload as Partial<Product>);
        commit(product);
        setServerCopy(created);
        navigate(`/admin/products/${created.product_id}`, { replace: true });
        flashSaved('Created');
      } else if (productId) {
        const updated = await updateProduct(productId, payload);
        setProduct(updated);
        setServerCopy(updated);
        commit(updated);
        flashSaved('Saved');
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const removeProduct = async () => {
    if (!productId) return;
    const ok = await confirm({
      title: `Delete "${product.name}"?`,
      body: 'The product and its variants are removed for good. This cannot be undone.',
      confirmLabel: 'Delete product',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await deleteProduct(productId);
      navigate('/admin/products');
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? e?.message ?? 'Delete failed');
    }
  };

  if (loading) {
    return <div className="text-text-light">Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {confirmDialog}
      {/* Stuck to the top so Save is reachable from anywhere in the form.
          See the matching header in OrderEditor for the offsets. */}
      <div
        className="sticky top-14 md:top-0 z-20 bg-background border-b border-background-dark
                   -mx-4 px-4 sm:-mx-6 sm:px-6 py-3
                   flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
      >
        <h1 className="text-2xl font-semibold truncate">
          {mode === 'new' ? 'New product' : product.name || 'Edit product'}
        </h1>
        <div className="flex items-center flex-wrap gap-2">
          {savedHint && <span className="text-sm text-green-700">{savedHint}</span>}
          {mode === 'edit' && (
            <button
              type="button"
              onClick={removeProduct}
              className="border border-red-200 text-red-600 rounded px-3 py-2 text-sm hover:bg-red-50"
            >
              Delete
            </button>
          )}
          <button
            type="button"
            onClick={saveProduct}
            disabled={saving || !product.name}
            className="bg-primary text-white rounded px-4 py-2 text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : mode === 'new' ? 'Create' : 'Save'}
          </button>
        </div>
      </div>

      <RestoredDraftBanner
        savedAt={restoredAt}
        onDiscard={() => discard(mode === 'new' ? blankProduct() : serverCopy)}
        discardsTo={mode === 'new' ? 'a blank form' : 'the saved product'}
      />

      {error && <div className="text-sm text-red-600">{error}</div>}

      <section className="bg-white border border-background-dark rounded p-4 space-y-3">
        <div>
          <label className="block text-xs text-text-light mb-1">Name</label>
          <input
            value={product.name ?? ''}
            onChange={e => setProduct({ ...product, name: e.target.value })}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-light mb-1">Slug</label>
            <input
              value={product.slug ?? ''}
              onChange={e => setProduct({ ...product, slug: e.target.value })}
              placeholder="auto from name if blank"
              className="w-full border border-background-dark rounded px-3 py-2 text-sm font-mono"
            />
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Category</label>
            <DropdownWithAddNew
              value={product.category ?? ''}
              options={masterOptions.master_categories}
              onChange={v => setProduct({ ...product, category: v })}
              onAddOption={v => addOption('master_categories', v)}
              placeholder="Uncategorized"
              className="w-full"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-text-light mb-1">Status</label>
            <select
              value={product.status ?? 'draft'}
              onChange={e =>
                setProduct({ ...product, status: e.target.value as Product['status'] })
              }
              className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Visibility</label>
            <select
              value={product.library_slug ?? ''}
              onChange={e => setProduct({ ...product, library_slug: e.target.value })}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm bg-white"
            >
              <option value="">Public shop</option>
              {libraries.map(l => (
                <option key={l.slug} value={l.slug}>
                  Private — {l.name}
                </option>
              ))}
            </select>
            <div className="text-xs text-text-light mt-1">
              {product.library_slug
                ? 'Only visible inside this private library.'
                : 'Listed in the public shop.'}
            </div>
          </div>
        </div>
        <div>
          <label className="block text-xs text-text-light mb-1">Description</label>
          <textarea
            value={product.description ?? ''}
            onChange={e => setProduct({ ...product, description: e.target.value })}
            rows={4}
            className="w-full border border-background-dark rounded px-3 py-2 text-sm"
          />
        </div>
      </section>

      {mode === 'edit' && (
        <ProductListingPreview
          product={product}
          libraryName={libraries.find(l => l.slug === product.library_slug)?.name}
        />
      )}

      <section className="bg-white border border-background-dark rounded p-4 space-y-4">
        <div className="text-sm font-medium">Pricing & stock</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-text-light mb-1">Base price ($)</label>
            <MoneyInput
              valueCents={product.price_cents ?? 0}
              onChange={cents => setProduct({ ...product, price_cents: cents })}
              className="w-full border border-background-dark rounded px-3 py-2 text-sm text-right"
            />
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Weight (oz)</label>
            <input
              type="number"
              step="0.1"
              min={0}
              value={product.weight_oz ?? 0}
              onChange={e =>
                setProduct({ ...product, weight_oz: Number(e.target.value) || 0 })
              }
              className="w-full border border-background-dark rounded px-3 py-2 text-sm text-right"
            />
          </div>
          <div>
            <label className="block text-xs text-text-light mb-1">Availability</label>
            <label className="inline-flex items-center gap-2 mt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={product.in_stock ?? true}
                onChange={e => setProduct({ ...product, in_stock: e.target.checked })}
              />
              <span className={`text-sm ${product.in_stock ?? true ? 'text-green-700' : 'text-text-light'}`}>
                {product.in_stock ?? true ? 'In stock' : 'Out of stock'}
              </span>
            </label>
          </div>
        </div>

        {(product.size_options ?? []).length > 0 && (
          <div className="border-t border-background-dark pt-3">
            <div className="text-xs font-medium text-text-light mb-2">
              Price per size (leave blank to use base price)
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(product.size_options ?? []).map(size => {
                const override = product.size_price_overrides?.[size];
                const displayValue =
                  override === undefined || override === null ? '' : centsToDollars(override);
                return (
                  <div key={size} className="flex items-center flex-wrap gap-2">
                    <span className="text-sm w-12 font-medium">{size}</span>
                    <div className="relative flex-1">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-text-light">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        min={0}
                        placeholder={centsToDollars(product.price_cents ?? 0)}
                        value={displayValue}
                        onChange={e => {
                          const raw = e.target.value;
                          const next = { ...(product.size_price_overrides ?? {}) };
                          if (raw === '') delete next[size];
                          else next[size] = dollarsToCents(raw);
                          setProduct({ ...product, size_price_overrides: next });
                        }}
                        className="w-full border border-background-dark rounded pl-5 pr-2 py-1 text-sm text-right"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <section className="bg-white border border-background-dark rounded p-4 space-y-5">
        <div>
          <div className="text-sm font-medium">Customer-selectable options</div>
          <p className="text-xs text-text-light mt-1">
            Add every size, color, thread color, and font you offer for this product.
            Customers see each list as a dropdown at checkout and pick one of each.
            Leave a list empty if it doesn't apply.
          </p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Sizes</label>
          <MultiSelectWithAddNew
            values={product.size_options ?? []}
            options={masterOptions.master_sizes}
            onChange={vals => setProduct({ ...product, size_options: vals })}
            onAddOption={v => addOption('master_sizes', v)}
            placeholder="Pick a size…"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Colors</label>
          <MultiSelectWithAddNew
            values={product.color_options ?? []}
            options={masterOptions.master_colors}
            onChange={vals => setProduct({ ...product, color_options: vals })}
            onAddOption={v => addOption('master_colors', v)}
            placeholder="Pick a color…"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Thread colors</label>
          <MultiSelectWithAddNew
            values={product.thread_color_options ?? []}
            options={masterOptions.master_thread_colors}
            onChange={vals => setProduct({ ...product, thread_color_options: vals })}
            onAddOption={v => addOption('master_thread_colors', v)}
            placeholder="Pick a thread color…"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Fonts</label>
          <MultiSelectWithAddNew
            values={product.font_options ?? []}
            options={masterOptions.master_fonts}
            onChange={vals => setProduct({ ...product, font_options: vals })}
            onAddOption={v => addOption('master_fonts', v)}
            placeholder="Pick a font…"
          />
        </div>
      </section>

      <section className="bg-white border border-background-dark rounded p-4">
        <ProductImagesEditor
          images={product.images ?? []}
          onChange={imgs => setProduct({ ...product, images: imgs })}
          productId={productId}
          colorOptions={product.color_options ?? []}
          colorMap={product.image_color_map ?? {}}
          onColorMapChange={map => setProduct({ ...product, image_color_map: map })}
        />
      </section>
    </div>
  );
};

export default ProductEditor;
